import React, { useEffect, useState } from 'react'
import './KaramaleTerms.css'
import Header3 from './components/Header3'
import Header from './components/Header'
import Header2 from './components/Header2'
import Header5 from './components/Header5'

const RefundLink = () => <a href="/refund-policy">Refund Policy</a>

const SECTIONS = [
  {
    id: 'acceptance',
    title: 'Acceptance of terms',
    body: (
      <p>
        By accessing or using the Karamale platform, purchasing tickets, or interacting with event
        listings, you agree to be bound by these Terms and Conditions. If you do not agree, please
        discontinue use.
      </p>
    ),
  },
  {
    id: 'usage',
    title: 'Platform usage',
    items: [
      <>You must be 18+ or have parental consent to use the platform.</>,
      <>No unlawful usage, including fraud, scalping, or event rule violations.</>,
      <>Karamale may suspend accounts that violate these terms.</>,
    ],
  },
  {
    id: 'purchase',
    title: 'Ticket purchase and delivery',
    items: [
      <>
        All purchases are final unless eligible under our <RefundLink />.
      </>,
      <>Digital tickets are delivered to your Karamale account as QR codes.</>,
      <>Actual (printed) tickets are only issued for events with offline support.</>,
    ],
  },
  {
    id: 'ownership',
    title: 'Ticket ownership and restrictions',
    items: [
      <>Tickets are non-transferable unless allowed by the organiser.</>,
      <>Resale or promotional use without consent is prohibited.</>,
      <>Tickets from unauthorised sellers may be invalid.</>,
    ],
  },
  {
    id: 'changes',
    title: 'Event changes and cancellations',
    items: [
      <>
        Organisers may alter event details. We'll notify affected buyers by email, SMS, or dashboard
        alerts.
      </>,
      <>
        Refer to our <RefundLink /> for cancellation procedures.
      </>,
    ],
  },
  {
    id: 'liability',
    title: 'Liability and disclaimer',
    items: [
      <>Karamale is not liable for event safety, quality, or third-party services.</>,
      <>Tickets lost, stolen, or damaged are the user's responsibility.</>,
      <>Use of the platform is at your own risk.</>,
    ],
  },
  {
    id: 'privacy',
    title: 'Privacy and data',
    body: (
      <p>
        By using Karamale, you consent to our data practices as outlined in the{' '}
        <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">
          Privacy Policy
        </a>
        . We do not sell personal data to third parties.
      </p>
    ),
  },
  {
    id: 'ip',
    title: 'Intellectual property',
    body: (
      <p>
        All content on the Karamale platform is owned by Karamale or its licensors. You may not copy
        or redistribute platform materials without written permission.
      </p>
    ),
  },
  {
    id: 'updates',
    title: 'Updates to terms',
    body: (
      <p>
        These Terms may be updated periodically. Continued use after changes constitutes acceptance.
      </p>
    ),
  },
]

function useActiveSection(ids) {
  const [active, setActive] = useState(ids[0])
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting)
        if (visible.length) {
          visible.sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)
          setActive(visible[0].target.id)
        }
      },
      { rootMargin: '-15% 0px -70% 0px' },
    )
    ids.forEach((id) => {
      const el = document.getElementById(id)
      if (el) obs.observe(el)
    })
    return () => obs.disconnect()
  }, [ids])
  return active
}

function KaramaleTerms() {
  useEffect(() => {
    document.title = 'Karamale Terms and Conditions'
  }, [])

  const active = useActiveSection(SECTIONS.map((s) => s.id))

  return (
    <>
      {localStorage.getItem('customername') !== null ? (
        <Header2 />
      ) : localStorage.getItem('businessname') !== null ? (
        <Header3 />
      ) : localStorage.getItem('partner') !== null ? (
        <Header5 />
      ) : (
        <Header />
      )}

      <main className="kt">
        <div className="kt-wrap">
          <header className="kt-hero">
            <h1>Karamale Terms and Conditions</h1>
            <p>The rules for using Karamale and buying tickets on it.</p>
          </header>

          <div className="kt-body">
            <nav className="kt-nav" aria-label="Terms sections">
              <ul>
                {SECTIONS.map(({ id, title }) => (
                  <li key={id}>
                    <a
                      href={`#${id}`}
                      className={active === id ? 'is-active' : ''}
                      aria-current={active === id ? 'true' : undefined}
                    >
                      {title}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>

            <article className="kt-content">
              {SECTIONS.map(({ id, title, items, body }, i) => (
                <section key={id} id={id} className="kt-section">
                  <h2>
                    <span className="kt-num">{i + 1}.</span>
                    {title}
                  </h2>
                  {body}
                  {items && (
                    <ul>
                      {items.map((item, j) => (
                        <li key={j}>{item}</li>
                      ))}
                    </ul>
                  )}
                </section>
              ))}
            </article>
          </div>
        </div>
      </main>
    </>
  )
}

export default KaramaleTerms
